import { TABLE_SECTION_ATTRIBUTE } from '~/components/document-editor/extensions/table-markup-extension';
import { extractStyleCSSBlocks, normalizeCSSBlock, STYLE_TAG_REGEX } from './editor-css';

const SECTION_TAGS: Record<string, string> = { head: 'thead', foot: 'tfoot' };

/**
 * Regroups the rows the editor marked as header or footer rows into <thead> and <tfoot>. The
 * editor's schema keeps every row in one <tbody>; the print engine repeats a header on each
 * page only when it sits in a <thead>. Untouched when no row is marked, so a document without
 * a header is not reserialized.
 */
export const restoreTableSections = (bodyHTML: string): string => {
  if (!bodyHTML.includes(TABLE_SECTION_ATTRIBUTE)) return bodyHTML;
  const doc = new DOMParser().parseFromString(`<body>${bodyHTML}</body>`, 'text/html');
  for (const row of Array.from(doc.querySelectorAll(`tr[${TABLE_SECTION_ATTRIBUTE}]`))) {
    const tag = SECTION_TAGS[row.getAttribute(TABLE_SECTION_ATTRIBUTE) ?? ''];
    row.removeAttribute(TABLE_SECTION_ATTRIBUTE);
    const table = row.closest('table');
    if (!tag || !table) continue;
    let section = table.querySelector(`:scope > ${tag}`);
    if (!section) {
      section = doc.createElement(tag);
      const body = table.querySelector(':scope > tbody');
      if (tag === 'thead') table.insertBefore(section, body);
      else table.appendChild(section);
    }
    section.appendChild(row);
  }
  for (const empty of Array.from(doc.querySelectorAll('tbody:empty'))) empty.remove();
  return doc.body.innerHTML;
};

/**
 * Merge TipTap body HTML with a full-document prefix/suffix and de-duplicate
 * body `<style>` blocks already present in the head prefix.
 */
export const mergeBodyHTMLForSave = (
  bodyHTML: string,
  prefix: string | null,
  suffix: string | null
): string => {
  bodyHTML = restoreTableSections(bodyHTML);
  if (prefix == null || suffix == null) return bodyHTML;

  const headCSS = extractStyleCSSBlocks(prefix);
  let dedupedBody = bodyHTML;
  if (headCSS.size > 0) {
    dedupedBody = bodyHTML.replace(STYLE_TAG_REGEX, (match, inner) => {
      const normalized = normalizeCSSBlock(String(inner));
      return normalized && headCSS.has(normalized) ? '' : match;
    });
  }
  return prefix + dedupedBody + suffix;
};
